import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Drag-resizable LABEL column on a `<PreviewTable variant="table-display">`, persisted per card.
 *
 * The FOURTH layout hook, and it follows `useColumnWidths`, `useRowHeight` and `useRailWidth`
 * exactly: same storage discipline, same clamp-on-read, same seed-from-the-MEASURED-element,
 * same `resizing` flag, same double-click reset with a `Reset width` entry in the cog beside it.
 *
 * **Why a card needed one.** The label column is 38% of the row, which suits a phrase and is
 * far too much for `Result` or `Remarks` — and the value beside it is the thing being read. A
 * card holding a bulleted list wrapped it into a third of the width while the word naming it
 * sat in a column six times wider than itself (product call, 21 Oct 2026).
 *
 * **`labelWidth` is the SEED, not a rival.** `default` (38%) and `narrow` (5rem) still decide
 * where the boundary starts and what a reset goes back to; this hook only moves it from there,
 * so a card nobody has dragged renders exactly as it always did.
 *
 * **The key is the CARD's id**, which already exists for the cog's own ids and is already
 * unique per profile section (`<module>-profile-<section>`). That is what makes this cost zero
 * call-site changes across 140 `<PreviewTable>`s — the one outside a card keeps its fixed width,
 * because a drag nobody can persist is one that vanishes on the next navigation.
 */

/** Narrower than this and a one-word label has no room beside its own padding. */
const MIN_LABEL_WIDTH = 96;

/**
 * The floor under the VALUE, not under the label — the value is what the card is read for, so
 * the drag stops before the half carrying the content becomes unreadable.
 */
const MIN_VALUE_WIDTH = 200;

function storageKey(key: string): string {
  return `profile.${key}.labelWidth`;
}

export interface PreviewLabelWidth {
  /** `null` means "untouched" — the CSS default from `labelWidth` is still in force. */
  width: number | null;
  resizing: boolean;
  startResize: (event: React.PointerEvent) => void;
  reset: () => void;
}

/**
 * Supplied by `<ProfileCard>`, read by `<PreviewTable>`.
 *
 * The same shape and the same reason as `ProfileCardSurfaceContext` beside it: a component
 * adapting to the surface it was rendered on, rather than 140 call sites each remembering to
 * pass a key they would have to invent.
 */
export const PreviewLabelWidthContext = createContext<PreviewLabelWidth | null>(null);

export function usePreviewLabelWidthContext(): PreviewLabelWidth | null {
  return useContext(PreviewLabelWidthContext);
}

export function usePreviewLabelWidth(key: string | undefined): PreviewLabelWidth {
  const [width, setWidth] = useState<number | null>(() => {
    if (!key) return null;

    try {
      const stored = localStorage.getItem(storageKey(key));
      if (stored === null) return null;

      const parsed = Number(stored);
      // Clamped on read: a stored value from an older floor must not survive as one.
      return Number.isFinite(parsed) ? Math.max(MIN_LABEL_WIDTH, parsed) : null;
    } catch {
      // Blocked or unparsable storage — fall through to the untouched default.
      return null;
    }
  });

  const [resizing, setResizing] = useState(false);
  const drag = useRef<{ startX: number; startWidth: number; maxWidth: number } | null>(null);

  useEffect(() => {
    if (!key) return;

    try {
      if (width === null) localStorage.removeItem(storageKey(key));
      else localStorage.setItem(storageKey(key), String(width));
    } catch {
      // A reader with site data blocked still gets the drag, just not the memory of it.
    }
  }, [key, width]);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const state = drag.current;
    if (!state) return;

    const next = state.startWidth + (event.clientX - state.startX);
    setWidth(Math.min(state.maxWidth, Math.max(MIN_LABEL_WIDTH, Math.round(next))));
  }, []);

  const onPointerUp = useCallback(() => {
    if (!drag.current) return;

    drag.current = null;
    setResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const startResize = useCallback((event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    /*
      Seeded from the MEASURED boundary rather than the stored number, for the reason the rail
      and the columns are: the two can differ — a clamp on read, a card that changed width, a
      percentage that resolves differently — and seeding from storage makes the column JUMP on
      the first pixel instead of tracking the pointer.

      The grip is absolutely positioned inside the `<dl>`, so its offsetLeft IS the boundary and
      the `<dl>`'s own width is the room the two columns share.
    */
    const grip = event.currentTarget as HTMLElement;
    const list = grip.parentElement;
    const listWidth = list?.getBoundingClientRect().width ?? 0;

    drag.current = {
      startX: event.clientX,
      startWidth: Math.round(grip.offsetLeft),
      // The value keeps its floor whatever the card's width — on a narrow card that can be
      // less than the label's own floor, so the max is held at or above it.
      maxWidth: Math.max(MIN_LABEL_WIDTH, Math.round(listWidth - MIN_VALUE_WIDTH)),
    };

    setResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  /** Back to `null`, so `labelWidth`'s own 38% or 5rem takes the boundary again. */
  const reset = useCallback(() => setWidth(null), []);

  return { width, resizing, startResize, reset };
}
